import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { UNINITIALIZED } from 'src/utils/Domain/Constants';
import { BoundPerspectiveAndTenant, TenantConfig } from 'configuration-schemas/bindings.types';
import { hotSwitchActivePerspective } from 'src/pages/NavigationShell/NavigationShell.slice';
import { defaultWorklistButtons } from 'configuration-schemas/confdefnView';
import type { AppThunk } from 'src/store.types';
import { logWarn } from '../loggingService';

export interface AppConfigSlice {
  tenantConfig: TenantConfig;
  configFingerprint?: string;
  confDefns: Record<string, BoundPerspectiveAndTenant>;
  isClientFacing: boolean;
}

const defaultConfig = {
  id: UNINITIALIZED,
  defaultTab: UNINITIALIZED,
  flowStatus: {
    values: [],
    defaultSelection: [],
  },
  fundedStatus: {
    values: [],
    defaultSelection: [],
  },
  lookBackPeriods: [],
  perspectives: [],
  placeholder: {
    text: 'PLACEHOLDER',
  },
  context: {
    assortment: { tabs: [], worklistConfigButtons: defaultWorklistButtons },
    hindsighting: { tabs: [], worklistConfigButtons: defaultWorklistButtons },
    allocation: { tabs: [], worklistConfigButtons: defaultWorklistButtons },
  },
  tabs: [],
  printSizes: [],
  canAddNewStyles: true,
  showBookmarks: false,
  disableScope: true,
  disableFilters: true,
  buttons: [], // This property is no longer used, but Zod v4 requires it to be initialized
  fabTypes: {},
  footerTypes: {},
  workflows: {},
} as TenantConfig;

export const initialState: AppConfigSlice = {
  tenantConfig: defaultConfig,
  confDefns: {},
  isClientFacing: true,
};

const appConfigSlice = createSlice({
  name: 'AppConfig',
  initialState,
  reducers: {
    updateAppConfig(state, action: PayloadAction<TenantConfig>) {
      state.tenantConfig = action.payload;
    },
    updateConfigFingerPrint(state, action: PayloadAction<string>) {
      state.tenantConfig = state.tenantConfig;
      state.configFingerprint = action.payload;
    },
    updateConfDefns(state, action: PayloadAction<Record<string, any>>) {
      state.confDefns = action.payload;
    },
    setIsClientFacing(state, action: PayloadAction<boolean>) {
      state.isClientFacing = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(hotSwitchActivePerspective, (state, action) => {
      state.tenantConfig = action.payload.selected.tenant;
    });
  },
});

export const { updateAppConfig, updateConfigFingerPrint, updateConfDefns, setIsClientFacing } = appConfigSlice.actions;

export default appConfigSlice.reducer;

export const applyFingerprintFromEvent =
  (incoming: string): AppThunk =>
  async (dispatch, getState) => {
    const fp = (incoming || '').trim();
    if (!fp) return;

    const current = getState().appConfig.configFingerprint ?? null;

    // Ignore if nothing actually changed
    if (current === fp) {
      return;
    }

    logWarn(`[fingerprint] applying new fingerprint →, ${fp}`);
    dispatch(updateConfigFingerPrint(fp));
  };
